import { renderPageShell } from '../lib/page-shell';
import { fetchCosmetics, type CosmeticRow } from '../lib/cosmetics';
import { fetchCatalog, fetchInventory, purchaseItem, type CatalogItem, type Rarity } from '../lib/catalog';
import { authorizedFetch, fetchMe } from '../lib/session';
import type { RouteHandler } from '../lib/router';

// Buyable cosmetics live here, and only here (owner decision, 2026-08-10) —
// achievement/level unlocks are earned progression (see achievements.ts),
// never bought. Two purchase paths exist side by side: the legacy signature-
// colour Stripe TEST MODE checkout below, and the C009 catalogue's direct
// soft-currency (Ticks) purchases in `renderCosmeticsStore`. Both are
// deliberately placeholder-art only — this page is the one that will carry
// real commissioned artist assets once they exist, never Cosmetic Factory's
// procedural output (that's reserved for achievement/level unlocks, to keep
// a real visual line between "free, earned" and "paid").

interface CheckoutResponse {
  sessionId: string;
  cosmetic: CosmeticRow;
  testMode: boolean;
  testCard?: string;
  checkoutUrl?: string;
}

export const renderStore: RouteHandler = (container, navigate, query) => {
  const body = renderPageShell(container, navigate, 'play', {
    title: 'STORE',
    subtitle: 'TEST MODE. No real account, no real card, no real money. This is a simulated checkout so the full purchase-to-cosmetic loop can be demonstrated safely.',
    body: `
      <h2 class="history-heading">Signature Colours</h2>
      <div id="store-mount"><p class="page-body__stub">Loading…</p></div>
      <h2 class="history-heading">Cosmetics</h2>
      <div id="cosmetics-store-mount"><p class="page-body__stub">Loading…</p></div>
    `,
  });

  const mount = body.querySelector<HTMLDivElement>('#store-mount')!;
  const cosmeticsMount = body.querySelector<HTMLDivElement>('#cosmetics-store-mount')!;

  renderCosmeticsStore(cosmeticsMount, navigate);

  async function renderCatalogue() {
    const me = await fetchMe();
    if (!me) {
      mount.innerHTML = `
        <div class="handoff">
          <p class="handoff__label">Sign in required</p>
          <h2>The store is tied to your account</h2>
          <button class="button button--primary" type="button" data-action="account">Go to account</button>
        </div>
      `;
      mount.querySelector<HTMLButtonElement>('[data-action="account"]')?.addEventListener('click', () => navigate('account'));
      return;
    }

    const { catalog, inventory } = await fetchCosmetics();
    const owned = new Set(inventory.map((row) => row.cosmetic_id));
    const purchasable = catalog.filter((row) => row.unlock_type === 'purchase');

    mount.innerHTML = `
      <div class="cosmetics-grid">
        ${purchasable
          .map(
            (cosmetic) => `
              <div class="choice-card cosmetic-card">
                <div class="cosmetic-card__swatch" style="background:${cosmetic.background_color}; border-color:${cosmetic.accent_color};">
                  <span style="background:${cosmetic.accent_color};"></span>
                </div>
                <strong>${cosmetic.name}</strong>
                <small>$${((cosmetic.price_cents ?? 0) / 100).toFixed(2)}</small>
                <button
                  class="button ${owned.has(cosmetic.id) ? 'button--primary' : 'button--secondary'}"
                  type="button"
                  data-action="${owned.has(cosmetic.id) ? '' : 'buy'}"
                  data-cosmetic-id="${cosmetic.id}"
                  ${owned.has(cosmetic.id) ? 'disabled' : ''}
                >
                  ${owned.has(cosmetic.id) ? 'Owned' : 'Buy'}
                </button>
              </div>
            `,
          )
          .join('')}
      </div>
    `;

    mount.querySelectorAll<HTMLButtonElement>('[data-action="buy"]').forEach((button) => {
      button.addEventListener('click', () => startCheckout(button.dataset.cosmeticId!));
    });
  }

  async function startCheckout(cosmeticId: string) {
    mount.innerHTML = `<div class="handoff"><p class="handoff__label">Starting checkout…</p></div>`;
    try {
      const checkout = await authorizedFetch<CheckoutResponse>('/api/store/checkout', {
        method: 'POST',
        body: JSON.stringify({ cosmeticId }),
      });
      if (checkout.testMode) {
        renderTestCheckout(checkout);
      } else if (checkout.checkoutUrl) {
        window.location.href = checkout.checkoutUrl;
      } else {
        renderError('Checkout did not return a payment link.');
      }
    } catch (error) {
      renderError(error instanceof Error ? error.message : 'Could not start checkout.');
    }
  }

  function renderTestCheckout(checkout: CheckoutResponse) {
    mount.innerHTML = `
      <div class="handoff">
        <p class="handoff__label">TEST MODE CHECKOUT. No real payment</p>
        <h2>${checkout.cosmetic.name}: $${((checkout.cosmetic.price_cents ?? 0) / 100).toFixed(2)}</h2>
        <p class="round-stage__hint">Simulated card: <strong>${checkout.testCard}</strong> (Stripe's public test number, no funds ever move).</p>
        <div class="hero-actions">
          <button class="button button--primary" type="button" data-action="confirm">Confirm test payment</button>
          <button class="button button--secondary" type="button" data-action="cancel">Cancel</button>
        </div>
      </div>
    `;
    mount.querySelector<HTMLButtonElement>('[data-action="confirm"]')?.addEventListener('click', async (event) => {
      (event.target as HTMLButtonElement).disabled = true;
      try {
        await authorizedFetch('/api/store/webhook', { method: 'POST', body: JSON.stringify({ sessionId: checkout.sessionId }) });
        renderSuccess(checkout.cosmetic);
      } catch (error) {
        renderError(error instanceof Error ? error.message : 'Could not complete the test purchase.');
      }
    });
    mount.querySelector<HTMLButtonElement>('[data-action="cancel"]')?.addEventListener('click', renderCatalogue);
  }

  function renderSuccess(cosmetic?: CosmeticRow) {
    mount.innerHTML = `
      <div class="handoff">
        <p class="handoff__label">Purchase complete</p>
        <h2>${cosmetic ? `${cosmetic.name} unlocked` : 'Cosmetic unlocked'}</h2>
        <div class="hero-actions">
          <button class="button button--primary" type="button" data-action="customise">Equip it</button>
          <button class="button button--secondary" type="button" data-action="store">Back to store</button>
        </div>
      </div>
    `;
    mount.querySelector<HTMLButtonElement>('[data-action="customise"]')?.addEventListener('click', () => navigate('customise'));
    mount.querySelector<HTMLButtonElement>('[data-action="store"]')?.addEventListener('click', renderCatalogue);
  }

  function renderError(message: string) {
    mount.innerHTML = `
      <div class="handoff">
        <p class="handoff__label">Error</p>
        <h2>${message}</h2>
        <button class="button button--secondary" type="button" data-action="back">Back to store</button>
      </div>
    `;
    mount.querySelector<HTMLButtonElement>('[data-action="back"]')?.addEventListener('click', renderCatalogue);
  }

  const purchaseStatus = query.get('purchase');
  if (purchaseStatus === 'success') {
    renderSuccess();
  } else if (purchaseStatus === 'cancelled') {
    mount.innerHTML = `<div class="handoff"><p class="handoff__label">Checkout cancelled</p></div>`;
    setTimeout(renderCatalogue, 1200);
  } else {
    renderCatalogue();
  }
};

/**
 * The C009 general catalogue's buyable items — soft-currency (Ticks)
 * purchases only; premium (Tocks) items have no purchase path yet, shown
 * disabled rather than hidden so the full priced catalogue is visible. Owned
 * items are hidden here — Inventory is where you manage what you already
 * have (owner decision, 2026-08-10: this page is buying only).
 */
async function renderCosmeticsStore(mount: HTMLDivElement, navigate: (route: string) => void): Promise<void> {
  // Held in closure so a purchase re-renders from memory rather than refetching
  // the whole catalogue + inventory + profile over the network (owner report
  // 2026-08-18: buying felt like nothing happened for several seconds). The
  // purchase POST returns the authoritative new balance, so the only round trip
  // on a buy is that single call.
  let items: CatalogItem[] = [];
  let rarityById = new Map<string, Rarity>();
  const owned = new Set<string>();
  let ticksBalance = 0;

  function renderList() {
    const buyable = items
      .filter((item) => item.active && !owned.has(item.id) && (item.priceSoft !== null || item.pricePremiumCents !== null))
      .sort((a, b) => (rarityById.get(a.rarity)?.rank ?? 0) - (rarityById.get(b.rarity)?.rank ?? 0) || a.name.localeCompare(b.name));

    if (buyable.length === 0) {
      mount.innerHTML = `<p class="settings-hint">Nothing left to buy. You own every priced cosmetic.</p>`;
      return;
    }

    mount.innerHTML = `
      <p class="settings-hint">${ticksBalance} Ticks available. <button class="button button--secondary" type="button" data-action="get-currency">Get more</button></p>
      <div class="cosmetics-grid">
        ${buyable.map((item) => renderBuyCard(item, rarityById.get(item.rarity), ticksBalance)).join('')}
      </div>
    `;

    mount.querySelector<HTMLButtonElement>('[data-action="get-currency"]')?.addEventListener('click', () => navigate('currency-store'));

    mount.querySelectorAll<HTMLButtonElement>('[data-action="buy"]').forEach((button) => {
      button.addEventListener('click', async () => {
        const itemId = button.dataset.itemId!;
        button.disabled = true;
        button.textContent = 'Buying…';
        try {
          const { balance } = await purchaseItem(itemId);
          owned.add(itemId);
          ticksBalance = balance;
          renderList();
        } catch (error) {
          button.disabled = false;
          button.textContent = error instanceof Error ? error.message : 'Could not buy.';
        }
      });
    });
  }

  async function render() {
    const me = await fetchMe();
    if (!me) {
      mount.innerHTML = `<p class="settings-hint">Sign in to buy cosmetics.</p>`;
      return;
    }

    const [catalog, inventory] = await Promise.all([fetchCatalog(), fetchInventory()]);
    items = catalog.items;
    rarityById = new Map<string, Rarity>(catalog.rarities.map((r) => [r.id, r]));
    owned.clear();
    inventory.owned.forEach((id) => owned.add(id));
    ticksBalance = inventory.currencies.find((c) => c.id === 'ticks')?.balance ?? 0;
    renderList();
  }

  function renderBuyCard(item: CatalogItem, rarity: Rarity | undefined, balance: number): string {
    const rarityColor = rarity?.color ?? '#9aa0a6';
    const rarityName = rarity?.name ?? item.rarity;
    const buyable = item.priceSoft !== null;
    const actionLabel = buyable ? `Buy: ${item.priceSoft} Ticks` : 'Premium (coming soon)';
    return `
      <div class="choice-card cosmetic-card">
        <div class="cosmetic-card__swatch" style="background:${rarityColor}22; border-color:${rarityColor};">
          <span style="background:${rarityColor};"></span>
        </div>
        <strong>${item.name}</strong>
        <small>${item.description}</small>
        <small style="color:${rarityColor};">${rarityName}</small>
        <button
          class="button button--secondary"
          type="button"
          data-action="${buyable ? 'buy' : ''}"
          data-item-id="${item.id}"
          ${buyable ? (balance < (item.priceSoft ?? 0) ? 'disabled' : '') : 'disabled'}
        >${actionLabel}</button>
      </div>
    `;
  }

  await render();
}
